import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Mic,
  MicOff,
  User,
  Search,
  X,
  CheckSquare,
  Target,
  Building2,
  FileText,
  Presentation,
  Loader2,
  ChevronDown,
  Sparkles,
  Calendar,
  Flag,
  Save,
  ExternalLink,
  Link2,
  Plus,
  AlertCircle
} from 'lucide-react';
import { Lead, Task, Company, Opportunity } from '../../types';
import { openaiService } from '../../services/openaiService';
import { tasksService } from '../../services/tasksService';
import { leadsService } from '../../services/leadsService';
import { companiesService } from '../../services/companiesService';

interface QuickSelectMenuProps {
  leads: Lead[];
  companies: Company[];
  opportunities: Opportunity[];
  currentUserId?: string;
  currentUserName?: string;
  orgId?: string;
  onCreateTask: (leadId: string, leadName: string) => void;
  onCreateOpportunity: (leadId: string, leadName: string) => void;
  onAssignCompany: (leadId: string, leadName: string) => void;
  onAddNote: (leadId: string, leadName: string) => void;
  onMeetingPrep: (leadId: string, leadName: string) => void;
  onTaskCreated?: () => void;
  onNoteAdded?: () => void;
  onNavigateToOpportunity?: (opportunity: Opportunity) => void;
  onNavigateToCompany?: (company: Company) => void;
  onNavigateToLead?: (lead: Lead) => void;
}

interface ActionButton {
  id: string;
  icon: React.ElementType;
  label: string;
  color: string;
  hoverColor: string;
  angle: number; // Position in degrees (0 = top, clockwise)
}

const QuickSelectMenu: React.FC<QuickSelectMenuProps> = ({
  leads,
  companies,
  opportunities,
  currentUserId,
  currentUserName,
  orgId,
  onCreateTask,
  onCreateOpportunity,
  onAssignCompany,
  onAddNote,
  onMeetingPrep,
  onTaskCreated,
  onNoteAdded,
  onNavigateToOpportunity,
  onNavigateToCompany,
  onNavigateToLead
}) => {
  const { t } = useTranslation('quickSelect');
  const { t: tc } = useTranslation('common');

  // State
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [showActions, setShowActions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // Task Modal State
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskDueDate, setTaskDueDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });
  const [taskPriority, setTaskPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [taskType, setTaskType] = useState<'call' | 'email' | 'meeting' | 'follow_up'>('follow_up');
  const [isSavingTask, setIsSavingTask] = useState(false);

  // Note Modal State
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  // Opportunity Modal State
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  
  // Company Modal State
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const [isLinkingCompany, setIsLinkingCompany] = useState(false);

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Find opportunities linked to the selected lead
  const leadOpportunities = useMemo(() => {
    if (!selectedLead) return [];
    return opportunities.filter(opp => 
      opp.leadIds?.includes(selectedLead.id) || 
      opp.leads?.some(l => l.id === selectedLead.id)
    );
  }, [selectedLead, opportunities]);

  // Find company linked to the selected lead
  const linkedCompany = useMemo(() => {
    if (!selectedLead?.companyId) return null;
    return companies.find(c => c.id === selectedLead.companyId) || null;
  }, [selectedLead, companies]);

  // Filter companies for search
  const filteredCompanies = useMemo(() => {
    if (!companySearchQuery.trim()) return companies.slice(0, 10);
    const query = companySearchQuery.toLowerCase();
    return companies
      .filter(company => 
        company.name.toLowerCase().includes(query) ||
        company.domain?.toLowerCase().includes(query) ||
        company.industry?.toLowerCase().includes(query)
      )
      .slice(0, 10);
  }, [companies, companySearchQuery]);

  // Action buttons configuration - positioned in a circle
  const actionButtons: ActionButton[] = [
    { id: 'task', icon: CheckSquare, label: t('actions.createTask'), color: 'bg-info', hoverColor: 'hover:bg-accent', angle: 270 }, // Top
    { id: 'opportunity', icon: Target, label: t('actions.createOpportunity'), color: 'bg-success', hoverColor: 'hover:bg-success-soft', angle: 342 }, // Top-right
    { id: 'company', icon: Building2, label: t('actions.assignCompany'), color: 'bg-accent', hoverColor: 'hover:bg-accent', angle: 54 }, // Bottom-right
    { id: 'note', icon: FileText, label: t('actions.addNote'), color: 'bg-warning', hoverColor: 'hover:bg-warning-soft', angle: 126 }, // Bottom-left
    { id: 'meeting-prep', icon: Presentation, label: t('actions.meetingPrep'), color: 'bg-danger', hoverColor: 'hover:bg-danger-soft', angle: 198 }, // Left
  ];

  // Fuzzy search for leads
  const filteredLeads = useMemo(() => {
    if (!searchQuery.trim()) return leads.slice(0, 10);
    
    const query = searchQuery.toLowerCase();
    
    // Support for German honorifics
    const normalizedQuery = query
      .replace(/herr\s*/i, '')
      .replace(/frau\s*/i, '')
      .replace(/mr\.?\s*/i, '')
      .replace(/mrs\.?\s*/i, '')
      .replace(/ms\.?\s*/i, '')
      .trim();

    return leads
      .filter(lead => {
        const firstName = lead.firstName || '';
        const lastName = lead.lastName || '';
        const fullName = `${firstName} ${lastName}`.toLowerCase();
        const reverseName = `${lastName} ${firstName}`.toLowerCase();
        const company = lead.company?.toLowerCase() || '';
        const email = lead.email?.toLowerCase() || '';

        return fullName.includes(normalizedQuery) ||
               reverseName.includes(normalizedQuery) ||
               firstName.toLowerCase().includes(normalizedQuery) ||
               lastName.toLowerCase().includes(normalizedQuery) ||
               company.includes(normalizedQuery) ||
               email.includes(normalizedQuery);
      })
      .slice(0, 10);
  }, [leads, searchQuery]);

  useEffect(() => {
    if (voiceTranscript) {
      setSearchQuery(voiceTranscript);
      setVoiceTranscript('');
    }
  }, [voiceTranscript]);

  // Focus search input when opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Voice recording functions
  const startVoiceRecording = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        try {
          setIsProcessing(true);
          const transcription = await openaiService.speechToText(audioBlob);
          setVoiceTranscript(transcription);
          setSearchQuery(transcription);
          setIsSearchOpen(true);
        } catch (error) {
          console.error('Transcription error:', error);
          setError(t('errors.voiceFailed'));
        } finally {
          setIsProcessing(false);
          stream.getTracks().forEach(track => track.stop());
        }
      };
      
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsListening(true);
    } catch (error) {
      console.error('Microphone access error:', error);
      setError(t('errors.microphoneAccess'));
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsListening(false);
    }
  };

  const toggleVoiceRecording = () => {
    if (isListening) {
      stopVoiceRecording();
    } else {
      startVoiceRecording();
    }
  };

  // Open Task Modal with pre-filled customer name
  const openTaskModal = (isCall: boolean = false) => {
    if (!selectedLead) return;
    const leadName = `${selectedLead.firstName || ''} ${selectedLead.lastName || ''}`.trim();
    setTaskTitle(isCall ? `Anruf: ${leadName}` : `Task für ${leadName}`);
    setTaskDescription('');
    setTaskType(isCall ? 'call' : 'follow_up');
    setTaskPriority('medium');
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setTaskDueDate(tomorrow.toISOString().split('T')[0]);
    setShowTaskModal(true);
  };

  // Open Note Modal
  const openNoteModal = () => {
    setNoteContent('');
    setShowNoteModal(true);
  };

  // Save Task
  const handleSaveTask = async () => {
    if (!selectedLead || !taskTitle.trim() || !orgId || !currentUserId) return;
    
    setIsSavingTask(true);
    try {
      const taskData: Omit<Task, 'id'> = {
        title: taskTitle,
        description: taskDescription,
        leadId: selectedLead.id,
        contactId: selectedLead.id,
        assignedTo: currentUserId,
        createdBy: currentUserId,
        visibility: 'private',
        orgId: orgId,
        dueDate: new Date(taskDueDate),
        completed: false,
        priority: taskPriority,
        type: taskType,
      };
      
      await tasksService.createTask(taskData);
      
      setShowTaskModal(false);
      showSuccess(`Task "${taskTitle}" erstellt!`);
      onTaskCreated?.();
    } catch (error) {
      console.error('Error creating task:', error);
      setError('Task konnte nicht erstellt werden');
    } finally {
      setIsSavingTask(false);
    }
  };

  // Save Note
  const handleSaveNote = async () => {
    if (!selectedLead || !noteContent.trim() || !currentUserId) return;
    
    setIsSavingNote(true);
    try {
      const leadName = `${selectedLead.firstName || ''} ${selectedLead.lastName || ''}`.trim();
      
      // Add note to lead
      await leadsService.addNoteToLead(selectedLead.id, {
        content: noteContent,
        createdBy: currentUserId,
        createdAt: new Date(),
        type: 'general'
      });
      
      setShowNoteModal(false);
      showSuccess(`Notiz für ${leadName} hinzugefügt!`);
      onNoteAdded?.();
    } catch (error) {
      console.error('Error adding note:', error);
      setError('Notiz konnte nicht hinzugefügt werden');
    } finally {
      setIsSavingNote(false);
    }
  };

  // Handle Opportunity action
  const handleOpportunityAction = () => {
    if (!selectedLead) return;
    
    if (leadOpportunities.length > 0) {
      // Lead has opportunities - show selection modal or navigate directly if only one
      if (leadOpportunities.length === 1) {
        onNavigateToOpportunity?.(leadOpportunities[0]);
      } else {
        setShowOpportunityModal(true);
      }
    } else {
      // No opportunities - create new one
      const leadName = `${selectedLead.firstName || ''} ${selectedLead.lastName || ''}`.trim();
      onCreateOpportunity(selectedLead.id, leadName);
    }
  };

  // Handle Company action
  const handleCompanyAction = () => {
    if (!selectedLead) return;
    
    if (linkedCompany) {
      // Lead has linked company - navigate to company
      onNavigateToCompany?.(linkedCompany);
    } else {
      // No company linked - show modal to link one
      setCompanySearchQuery('');
      setShowCompanyModal(true);
    }
  };

  // Link company to lead
  const handleLinkCompany = async (company: Company) => {
    if (!selectedLead) return;
    
    setIsLinkingCompany(true);
    try {
      await leadsService.updateLead(selectedLead.id, {
        companyId: company.id,
        company: company.name
      });
      
      // Update local state
      setSelectedLead({
        ...selectedLead,
        companyId: company.id,
        company: company.name
      });
      
      setShowCompanyModal(false);
      showSuccess(`${company.name} mit ${selectedLead.firstName || ''} ${selectedLead.lastName || ''} verknüpft!`);
    } catch (error) {
      console.error('Error linking company:', error);
      setError('Company konnte nicht verknüpft werden');
    } finally {
      setIsLinkingCompany(false);
    }
  };

  // Handle action button click
  const handleActionClick = (actionId: string) => {
    if (!selectedLead) return;
    
    const leadName = `${selectedLead.firstName || ''} ${selectedLead.lastName || ''}`.trim();
    
    switch (actionId) {
      case 'task':
        openTaskModal(false);
        break;
      case 'opportunity':
        handleOpportunityAction();
        break;
      case 'company':
        handleCompanyAction();
        break;
      case 'note':
        openNoteModal();
        break;
      case 'meeting-prep':
        // Navigate to meeting prep page with the selected lead
        if (selectedLead) {
          onMeetingPrep(selectedLead.id, `${selectedLead.firstName || ''} ${selectedLead.lastName || ''}`.trim());
        }
        break;
    }
  };

  // Handle lead selection
  const handleSelectLead = (lead: Lead) => {
    setSelectedLead(lead);
    setShowActions(true);
    setIsSearchOpen(false);
    setSearchQuery('');
  };

  // Reset selection
  const handleResetSelection = () => {
    setSelectedLead(null);
    setShowActions(false);
    setSearchQuery('');
    setVoiceTranscript('');
  };

  // Calculate button position
  const getButtonPosition = (angle: number, radius: number) => {
    const radian = (angle * Math.PI) / 180;
    const x = Math.cos(radian) * radius;
    const y = Math.sin(radian) * radius;
    return { x, y };
  };

  // Show success message briefly
  const showSuccess = (message: string) => {
    setActionSuccess(message);
    setTimeout(() => setActionSuccess(null), 3000);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-gradient-to-br from-bg-surface bg-info-soft to-info">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-accent shadow-card shadow-accent/20 mb-4">
          <Sparkles className="w-8 h-8 text-txt-inverted" />
        </div>
        <h1 className="text-3xl font-bold text-txt mb-2">{t('title')}</h1>
        <p className="text-txt-secondary">{t('subtitle')}</p>
      </div>

      {/* Success Message */}
      {actionSuccess && (
        <div className="mb-6 px-4 py-3 bg-success-soft border border-success rounded-xl text-success text-sm flex items-center animate-fade-in">
          <CheckSquare className="w-4 h-4 mr-2 flex-shrink-0" />
          {actionSuccess}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-6 px-4 py-3 bg-danger-soft border border-danger rounded-xl text-danger text-sm flex items-center">
          <X className="w-4 h-4 mr-2 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-danger-medium rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Radial Menu Container */}
      <div className="relative w-[400px] h-[400px] flex items-center justify-center">
        
        {/* Action Buttons - Outer Ring */}
        {showActions && selectedLead && actionButtons.map((action, index) => {
          const radius = 150; // Distance from center
          const pos = getButtonPosition(action.angle, radius);
          
          return (
            <button
              key={action.id}
              onClick={() => handleActionClick(action.id)}
              className={`absolute w-20 h-20 rounded-full ${action.color} ${action.hoverColor} text-txt-inverted shadow-card 
                         transform transition-all duration-300 hover:scale-110 hover:shadow-card
                         flex flex-col items-center justify-center gap-1
                         animate-[fadeInScale_0.3s_ease-out_forwards]`}
              style={{
                left: `calc(50% + ${pos.x}px - 40px)`,
                top: `calc(50% + ${pos.y}px - 40px)`,
                animationDelay: `${index * 0.05}s`,
                opacity: 0
              }}
              title={action.label}
            >
              <action.icon className="w-6 h-6" />
              <span className="text-[10px] font-medium text-center leading-tight px-1">
                {action.label.split(' ')[0]}
              </span>
            </button>
          );
        })}

        {/* Connection Lines (when actions are shown) */}
        {showActions && selectedLead && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
            {actionButtons.map((action) => {
              const pos = getButtonPosition(action.angle, 150);
              return (
                <line
                  key={`line-${action.id}`}
                  x1="50%"
                  y1="50%"
                  x2={`calc(50% + ${pos.x}px)`}
                  y2={`calc(50% + ${pos.y}px)`}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray="4 4"
                  className="text-txt-muted"
                  style={{
                    animation: 'drawLine 0.5s ease-out forwards'
                  }}
                />
              );
            })}
          </svg>
        )}

        {/* Center - Customer Selection */}
        <div className="relative z-10">
          {selectedLead ? (
            // Selected Lead Display
            <div 
              className="w-40 h-40 rounded-full bg-bg-card shadow-heavy border-4 border-accent 
                        flex flex-col items-center justify-center cursor-pointer
                        hover:border-accent transition-all duration-300 group"
              onClick={handleResetSelection}
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-accent to-accent 
                            flex items-center justify-center text-txt-inverted text-2xl font-bold mb-2
                            group-hover:scale-105 transition-transform">
                {(selectedLead.firstName || '?')[0]}{(selectedLead.lastName || '?')[0]}
              </div>
              <p className="text-sm font-semibold text-txt text-center px-2 truncate max-w-full">
                {selectedLead.firstName || ''} {selectedLead.lastName || ''}
              </p>
              <p className="text-xs text-txt-tertiary truncate max-w-full px-2">
                {selectedLead.company || ''}
              </p>
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-bg-surface rounded-full text-[10px] text-txt-tertiary">
                {t('clickToChange')}
              </div>
            </div>
          ) : (
            // Customer Selection UI
            <div className="w-40 h-40 rounded-full bg-bg-card shadow-heavy border-4 border-dashed border-strong 
                          flex flex-col items-center justify-center relative overflow-hidden">
              {isProcessing ? (
                // Processing state
                <div className="flex flex-col items-center">
                  <Loader2 className="w-10 h-10 text-accent animate-spin mb-2" />
                  <p className="text-xs text-txt-tertiary">{t('processing')}</p>
                </div>
              ) : isListening ? (
                // Listening state
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-danger rounded-full animate-ping opacity-25" />
                    <div className="relative w-16 h-16 bg-danger rounded-full flex items-center justify-center cursor-pointer"
                         onClick={toggleVoiceRecording}>
                      <MicOff className="w-8 h-8 text-txt-inverted" />
                    </div>
                  </div>
                  <p className="text-xs text-txt-tertiary mt-2">{t('listening')}</p>
                </div>
              ) : (
                // Default state - Choose customer
                <div className="flex flex-col items-center">
                  <button
                    onClick={() => setIsSearchOpen(true)}
                    className="w-16 h-16 bg-bg-surface hover:bg-bg-surface-hover rounded-full flex items-center justify-center 
                             transition-all duration-200 group mb-2"
                  >
                    <User className="w-8 h-8 text-txt-muted group-hover:text-txt-secondary" />
                  </button>
                  <p className="text-sm font-medium text-txt-secondary mb-2">{t('selectCustomer')}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsSearchOpen(true)}
                      className="p-2 bg-bg-surface hover:bg-bg-surface-hover rounded-lg transition-colors"
                      title="Suchen"
                    >
                      <Search className="w-4 h-4 text-txt-secondary" />
                    </button>
                    <button
                      onClick={toggleVoiceRecording}
                      className="p-2 bg-accent-soft hover:bg-accent-medium rounded-lg transition-colors"
                      title="Sprachsuche"
                    >
                      <Mic className="w-4 h-4 text-accent" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Search Dropdown */}
      {isSearchOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => setIsSearchOpen(false)}>
          <div 
            className="bg-bg-card rounded-2xl shadow-heavy w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Header */}
            <div className="p-4 border-b border-default">
              <div className="flex items-center gap-3">
                <Search className="w-5 h-5 text-txt-muted" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('searchPlaceholder')}
                  className="flex-1 text-lg outline-none placeholder:text-txt-muted"
                />
                <button
                  onClick={toggleVoiceRecording}
                  className={`p-2 rounded-lg transition-colors ${
                    isListening 
                      ? 'bg-danger-soft text-danger' 
                      : 'bg-accent-soft text-accent hover:bg-accent-medium'
                  }`}
                >
                  {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => setIsSearchOpen(false)}
                  className="p-2 hover:bg-bg-surface rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-txt-muted" />
                </button>
              </div>
              {voiceTranscript && (
                <p className="mt-2 text-sm text-accent italic">
                  {t('recognized')}: "{voiceTranscript}"
                </p>
              )}
            </div>

            {/* Search Results */}
            <div className="max-h-80 overflow-y-auto">
              {filteredLeads.length === 0 ? (
                <div className="p-8 text-center text-txt-tertiary">
                  <User className="w-12 h-12 mx-auto mb-3 text-txt-muted" />
                  <p>{t('noCustomerFound')}</p>
                  <p className="text-sm mt-1">{t('tryDifferentSearch')}</p>
                </div>
              ) : (
                <div className="py-2">
                  {filteredLeads.map((lead) => (
                    <button
                      key={lead.id}
                      onClick={() => handleSelectLead(lead)}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-bg-surface transition-colors text-left"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent to-accent 
                                    flex items-center justify-center text-txt-inverted font-medium">
                        {(lead.firstName || '?')[0]}{(lead.lastName || '?')[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-txt truncate">
                          {lead.firstName || ''} {lead.lastName || ''}
                        </p>
                        <p className="text-sm text-txt-tertiary truncate">
                          {lead.company || ''} • {lead.email || ''}
                        </p>
                      </div>
                      <ChevronDown className="w-5 h-5 text-txt-muted rotate-[-90deg]" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-12 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-bg-card/80 backdrop-blur rounded-full shadow-card border border-default">
          <Mic className="w-4 h-4 text-accent" />
          <span className="text-sm text-txt-secondary">
            {t('voiceTip')}
          </span>
        </div>
      </div>

      {/* Task Modal */}
      {showTaskModal && selectedLead && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => setShowTaskModal(false)}>
          <div 
            className="bg-bg-card rounded-2xl shadow-heavy w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-5 border-b border-default bg-gradient-to-r from-accent to-info">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-bg-card/20 rounded-xl flex items-center justify-center">
                    <CheckSquare className="w-5 h-5 text-txt-inverted" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-txt-inverted">Task erstellen</h2>
                    <p className="text-info text-sm">
                      für {selectedLead.firstName || ''} {selectedLead.lastName || ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowTaskModal(false)}
                  className="p-2 hover:bg-bg-card/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-txt-inverted" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">
                  Titel *
                </label>
                <input
                  type="text"
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  className="w-full px-4 py-2.5 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all"
                  placeholder="Task-Titel eingeben..."
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">
                  Beschreibung
                </label>
                <textarea
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all resize-none"
                  placeholder="Beschreibung hinzufügen..."
                />
              </div>

              {/* Due Date & Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-txt-secondary mb-1.5">
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Fällig am
                  </label>
                  <input
                    type="date"
                    value={taskDueDate}
                    onChange={(e) => setTaskDueDate(e.target.value)}
                    className="w-full px-4 py-2.5 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-txt-secondary mb-1.5">
                    Typ
                  </label>
                  <select
                    value={taskType}
                    onChange={(e) => setTaskType(e.target.value as any)}
                    className="w-full px-4 py-2.5 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-accent transition-all"
                  >
                    <option value="call">Anruf</option>
                    <option value="email">E-Mail</option>
                    <option value="meeting">Meeting</option>
                    <option value="follow_up">Follow-up</option>
                  </select>
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">
                  <Flag className="w-4 h-4 inline mr-1" />
                  Priorität
                </label>
                <div className="flex gap-2">
                  {(['low', 'medium', 'high'] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setTaskPriority(p)}
                      className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                        taskPriority === p
                          ? p === 'high'
                            ? 'bg-danger text-txt-inverted'
                            : p === 'medium'
                            ? 'bg-warning text-txt-inverted'
                            : 'bg-success text-txt-inverted'
                          : 'bg-bg-surface text-txt-secondary hover:bg-bg-surface-hover'
                      }`}
                    >
                      {p === 'high' ? 'Hoch' : p === 'medium' ? 'Mittel' : 'Niedrig'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Customer Info */}
              <div className="bg-bg-surface rounded-xl p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent to-accent flex items-center justify-center text-txt-inverted font-medium">
                  {(selectedLead.firstName || '?')[0]}{(selectedLead.lastName || '?')[0]}
                </div>
                <div>
                  <p className="font-medium text-txt">
                    {selectedLead.firstName || ''} {selectedLead.lastName || ''}
                  </p>
                  <p className="text-sm text-txt-tertiary">{selectedLead.company || ''}</p>
                </div>
                <div className="ml-auto px-2 py-1 bg-info-soft text-accent rounded-full text-xs font-medium">
                  Verknüpft
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-default bg-bg-surface flex justify-end gap-3">
              <button
                onClick={() => setShowTaskModal(false)}
                className="px-5 py-2.5 text-txt-secondary hover:bg-bg-surface-hover rounded-xl transition-colors font-medium"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveTask}
                disabled={!taskTitle.trim() || isSavingTask}
                className="px-5 py-2.5 bg-info hover:bg-accent text-txt-inverted rounded-xl transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingTask ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Task erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note Modal */}
      {showNoteModal && selectedLead && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => setShowNoteModal(false)}>
          <div 
            className="bg-bg-card rounded-2xl shadow-heavy w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-5 border-b border-default bg-gradient-to-r from-warning to-warning">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-bg-card/20 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-txt-inverted" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-txt-inverted">Notiz hinzufügen</h2>
                    <p className="text-warning text-sm">
                      für {selectedLead.firstName || ''} {selectedLead.lastName || ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowNoteModal(false)}
                  className="p-2 hover:bg-bg-card/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-txt-inverted" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4">
              {/* Note Content */}
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1.5">
                  Notiz *
                </label>
                <textarea
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  rows={5}
                  className="w-full px-4 py-2.5 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-warning transition-all resize-none"
                  placeholder="Notiz eingeben..."
                  autoFocus
                />
              </div>

              {/* Customer Info */}
              <div className="bg-bg-surface rounded-xl p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent to-accent flex items-center justify-center text-txt-inverted font-medium">
                  {(selectedLead.firstName || '?')[0]}{(selectedLead.lastName || '?')[0]}
                </div>
                <div>
                  <p className="font-medium text-txt">
                    {selectedLead.firstName || ''} {selectedLead.lastName || ''}
                  </p>
                  <p className="text-sm text-txt-tertiary">{selectedLead.company || ''}</p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-default bg-bg-surface flex justify-end gap-3">
              <button
                onClick={() => setShowNoteModal(false)}
                className="px-5 py-2.5 text-txt-secondary hover:bg-bg-surface-hover rounded-xl transition-colors font-medium"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveNote}
                disabled={!noteContent.trim() || isSavingNote}
                className="px-5 py-2.5 bg-warning hover:bg-warning-soft text-txt-inverted rounded-xl transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingNote ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Notiz speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Opportunity Selection Modal */}
      {showOpportunityModal && selectedLead && leadOpportunities.length > 0 && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => setShowOpportunityModal(false)}>
          <div 
            className="bg-bg-card rounded-2xl shadow-heavy w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-5 border-b border-default bg-gradient-to-r from-success to-success0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-bg-card/20 rounded-xl flex items-center justify-center">
                    <Target className="w-5 h-5 text-txt-inverted" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-txt-inverted">Opportunities</h2>
                    <p className="text-success text-sm">
                      für {selectedLead.firstName || ''} {selectedLead.lastName || ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowOpportunityModal(false)}
                  className="p-2 hover:bg-bg-card/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-txt-inverted" />
                </button>
              </div>
            </div>

            {/* Opportunities List */}
            <div className="max-h-80 overflow-y-auto">
              {leadOpportunities.map((opp) => (
                <button
                  key={opp.id}
                  onClick={() => {
                    setShowOpportunityModal(false);
                    onNavigateToOpportunity?.(opp);
                  }}
                  className="w-full px-5 py-4 flex items-center gap-4 hover:bg-bg-surface transition-colors text-left border-b border-subtle last:border-0"
                >
                  <div className={`w-3 h-3 rounded-full ${
                    opp.stage === 'won' ? 'bg-success' :
                    opp.stage === 'lost' ? 'bg-danger' :
                    opp.stage === 'negotiation' ? 'bg-warning' :
                    'bg-info'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-txt truncate">{opp.title}</p>
                    <p className="text-sm text-txt-tertiary">
                      {opp.stage} • {opp.value ? `€${opp.value.toLocaleString()}` : 'Kein Wert'}
                    </p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-txt-muted" />
                </button>
              ))}
            </div>

            {/* Footer - Create New */}
            <div className="p-4 border-t border-default bg-bg-surface">
              <button
                onClick={() => {
                  setShowOpportunityModal(false);
                  const leadName = `${selectedLead.firstName || ''} ${selectedLead.lastName || ''}`.trim();
                  onCreateOpportunity(selectedLead.id, leadName);
                }}
                className="w-full py-2.5 px-4 bg-success hover:bg-success text-txt-inverted rounded-xl transition-colors font-medium flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Neue Opportunity erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company Link Modal */}
      {showCompanyModal && selectedLead && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => setShowCompanyModal(false)}>
          <div 
            className="bg-bg-card rounded-2xl shadow-heavy w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-5 border-b border-default bg-gradient-to-r from-accent to-accent">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-bg-card/20 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-txt-inverted" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-txt-inverted">Company verknüpfen</h2>
                    <p className="text-accent text-sm">
                      für {selectedLead.firstName || ''} {selectedLead.lastName || ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCompanyModal(false)}
                  className="p-2 hover:bg-bg-card/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-txt-inverted" />
                </button>
              </div>
            </div>

            {/* Info Banner */}
            <div className="p-4 bg-warning-soft border-b border-warning">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-warning font-medium">Keine Company verknüpft</p>
                  <p className="text-sm text-warning">
                    Wählen Sie eine bestehende Company aus oder erstellen Sie eine neue.
                  </p>
                </div>
              </div>
            </div>

            {/* Search */}
            <div className="p-4 border-b border-default">
              <div className="flex items-center gap-3 bg-bg-surface rounded-xl px-4 py-2.5">
                <Search className="w-5 h-5 text-txt-muted" />
                <input
                  type="text"
                  value={companySearchQuery}
                  onChange={(e) => setCompanySearchQuery(e.target.value)}
                  placeholder="Company suchen..."
                  className="flex-1 bg-transparent outline-none text-txt placeholder:text-txt-muted"
                />
              </div>
            </div>

            {/* Companies List */}
            <div className="max-h-64 overflow-y-auto">
              {filteredCompanies.length === 0 ? (
                <div className="p-8 text-center text-txt-tertiary">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-txt-muted" />
                  <p>Keine Companies gefunden</p>
                </div>
              ) : (
                filteredCompanies.map((company) => (
                  <button
                    key={company.id}
                    onClick={() => handleLinkCompany(company)}
                    disabled={isLinkingCompany}
                    className="w-full px-5 py-3 flex items-center gap-3 hover:bg-bg-surface transition-colors text-left border-b border-subtle last:border-0 disabled:opacity-50"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-accent flex items-center justify-center text-txt-inverted font-medium">
                      {company.name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-txt truncate">{company.name}</p>
                      <p className="text-sm text-txt-tertiary truncate">
                        {company.industry || company.domain || 'Keine Branche'}
                      </p>
                    </div>
                    <Link2 className="w-4 h-4 text-txt-muted" />
                  </button>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-default bg-bg-surface flex gap-3">
              <button
                onClick={() => setShowCompanyModal(false)}
                className="flex-1 py-2.5 px-4 text-txt-secondary hover:bg-bg-surface-hover rounded-xl transition-colors font-medium"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  setShowCompanyModal(false);
                  onNavigateToLead?.(selectedLead);
                }}
                className="flex-1 py-2.5 px-4 bg-accent hover:bg-accent text-txt-inverted rounded-xl transition-colors font-medium flex items-center justify-center gap-2"
              >
                <User className="w-4 h-4" />
                Kontakt-Details öffnen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes drawLine {
          from {
            stroke-dashoffset: 100;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default QuickSelectMenu;

